import { combineEpics } from 'redux-observable';
import { Observable } from 'rxjs';
import kebabCase from 'lodash/kebabCase';

import * as ActionCreators from '../actions/ActionCreators';
import * as ActionTypes from '../actions/ActionTypes';
import * as Controls from './UIControlsNames';
import * as VideoTypes from '../player/videoSource/videoTypes';
import { inIframe, timecodeFromSeconds } from '../utils/ASVPUtils';
import { ASControl, ASFullscreenToggle, ASPlayToggle, ASMuteToggle } from './VideoJSComponents';
import fullscreenSVG from './icons/fullscreen.svg';
import exitFullscreenSVG from './icons/exit-fullscreen.svg';
import volumeSVG from './icons/volume.svg';
import mutedSVG from './icons/muted.svg';
import playSVG from './icons/play.svg';
import pauseSVG from './icons/pause.svg';
import ccSVG from './icons/cc.svg';
import closeSVG from './icons/close.svg';
import asSVG from './icons/as.svg';

import {
    VIDEOS_SITE_URL,
    FORBIDDEN_SLATE,
    NOT_FOUND_SLATE,
    PROBLEM_SLATE,
    RECONNECTING_SLATE,
} from '../constants';


/* add custom controls to control bar */
const controlsInitEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.INIT)
    .switchMap(() => {
        ASControl(Controls.VOLUME_CONTROL, 'as-volume-control vjs-control');
        ASControl(Controls.VOLUME_SLIDER_WRAPPER, 'as-volume-slider-wrapper');
        ASControl(Controls.VOLUME_SLIDER, 'as-volume-slider');
        ASControl(Controls.VOLUME_BAR, 'as-volume-bar');
        ASControl(Controls.SHARE_CONTROL, 'as-share-control vjs-control', '<div class="as-share">Share</div>');
        ASControl(Controls.ADULT_SWIM_RETURN, 'as-icon-control vjs-control', asSVG);
        ASFullscreenToggle(Controls.FULLSCREEN_TOGGLE, 'as-fullscreen-control vjs-control vjs-button', fullscreenSVG);
        ASPlayToggle(Controls.PLAY_TOGGLE, 'as-play-control vjs-control vjs-button', playSVG);
        ASMuteToggle(Controls.MUTE_TOGGLE, 'as-mute-control vjs-control vjs-button', volumeSVG);
        ASControl(Controls.CLOSE_BUTTON, 'close-button', closeSVG);
        ASControl(Controls.AD_COUNTDOWN, 'as-ad-countdown', 'AD: <span class="time-remaining"></span>');

        const volumeControl = player.controlBar.addChild(Controls.VOLUME_CONTROL);
        const sliderWrapper = volumeControl.addChild(Controls.VOLUME_SLIDER_WRAPPER);
        const slider = sliderWrapper.addChild(Controls.VOLUME_SLIDER);
        slider.addChild(Controls.VOLUME_BAR);
        player.controlBar.addChild(Controls.FULLSCREEN_TOGGLE);
        player.controlBar.addChild(Controls.PLAY_TOGGLE);
        const captionsControl = player.controlBar.getChild(Controls.CAPTIONS_CONTROL);
        captionsControl.el().querySelector('.vjs-icon-placeholder').innerHTML = ccSVG;
        const subtitlesControl = player.controlBar.getChild(Controls.SUBTITLES_CONTROL);
        subtitlesControl.el().querySelector('.vjs-icon-placeholder').innerHTML = ccSVG;
        const muteToggle = player.controlBar.addChild(Controls.MUTE_TOGGLE);

        return Observable.merge(
            Observable.fromEvent(muteToggle, 'mouseover').mapTo(ActionCreators.onVolumeControlMouseOver()),
            Observable.fromEvent(muteToggle, 'mouseout').mapTo(ActionCreators.onVolumeControlMouseOut()),
            Observable.fromEvent(volumeControl, 'mouseover').mapTo(ActionCreators.onVolumeControlMouseOver()),
            Observable.fromEvent(volumeControl, 'mouseout').mapTo(ActionCreators.onVolumeControlMouseOut()),
            Observable.fromEvent(slider, 'mousedown').map((e) => ActionCreators.onVolumeSliderMouseDown(e)),
        );
    });


// initialization for share control
const shareControlInitEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.INIT)
    .filter(() => !!options.share) // don't show if options force no share
    .switchMap(() => {
        ASControl(Controls.SHARE_CONTROL, 'as-share-control vjs-control', '<div class="as-share">Share</div>');

        const shareControl = player.controlBar.addChild(Controls.SHARE_CONTROL);

        return Observable.merge(
            Observable.fromEvent(shareControl, 'click').mapTo(ActionCreators.onShareControlClick()),
        );
    });


/* adult swim return home control */
const controlsAdultSwimReturnEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.SET_VIDEO_SOURCE)
    .filter(() => inIframe())
    .switchMap(() => {
        const adultSwimControl = player.controlBar.addChild(Controls.ADULT_SWIM_RETURN);
        const { collectionTitle, title } = store.getState().videoSource;
        const url = VIDEOS_SITE_URL + kebabCase(collectionTitle) + '/' + kebabCase(title) + '/';

        return Observable.fromEvent(adultSwimControl, 'click').map((e) => {
            const currentTime = player.currentTime();
            const duration = player.duration();
            const timecode = (currentTime !== duration) ? '?t=' + Math.round(currentTime) : '';

            player.pause();

            window.open(url + timecode, '_blank');
        }).ignoreElements();
    });
/* --- */


/* fullscreen icons */
const controlsFullscreenEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_FULLSCREEN_CHANGE)
    .map(() => player.isFullscreen())
    .do(isFullscreen => {
        const fullscreenToggleEl = player.controlBar.getChild(Controls.FULLSCREEN_TOGGLE).contentEl();

        if (isFullscreen) fullscreenToggleEl.innerHTML = exitFullscreenSVG;
        else fullscreenToggleEl.innerHTML = fullscreenSVG;
    })
    .ignoreElements();
/* --- */


/* mute/muted icons */
const controlsMuteEpic = (action$, store, { player }) => Observable.merge(
        action$.ofType(ActionTypes.PLAYER_EVENT_VOLUME_CHANGE),
        action$.ofType(ActionTypes.INIT)
    )
    .map(() => player.muted())
    .do(isMuted => {
        const muteToggleEl = player.controlBar.getChild(Controls.MUTE_TOGGLE).contentEl();

        if (isMuted) muteToggleEl.innerHTML = mutedSVG;
        else muteToggleEl.innerHTML = volumeSVG;
    })
    .ignoreElements();
/* --- */


/* play/pause icons */
const controlsPauseEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_PAUSE)
    .do(() => {
        const playToggleEl = player.controlBar.getChild(Controls.PLAY_TOGGLE).contentEl();
        playToggleEl.innerHTML = playSVG;
    })
    .ignoreElements();


const controlsPlayEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_PLAY)
    .do(() => {
        const playToggleEl = player.controlBar.getChild(Controls.PLAY_TOGGLE).contentEl();
        playToggleEl.innerHTML = pauseSVG;
    })
    .ignoreElements();
/* --- */


/* volume slider */
const onVolumeControlMouseOutEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.ON_VOLUME_CONTROL_MOUSE_OUT)
    .do(() => player.controlBar.getChild(Controls.VOLUME_CONTROL).removeClass('show-volume-control')) // hides the volume slider
    .ignoreElements();


const onVolumeControlMouseOverEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.ON_VOLUME_CONTROL_MOUSE_OVER)
    .do(() => player.controlBar.getChild(Controls.VOLUME_CONTROL).addClass('show-volume-control')) // shows the volume slider
    .ignoreElements();


const onDocumentMouseMoveEpic = (action$, store, { player }) => Observable.fromEvent(document, 'mousemove')
    .filter(() => store.getState().player.isVolumeDragging)
    .pluck('pageX')
    .map(ActionCreators.updateVolumeFromDrag);


const onDocumentMouseUpEpic = (action$, store, { player }) => Observable.fromEvent(document, 'mouseup')
    .switchMap(e => Observable.merge(
        Observable.of(ActionCreators.setIsVolumeDraggingState(false)).filter(() => store.getState().player.isVolumeDragging),
        Observable.of(e.pageX)
            .filter(() => store.getState().player.isVolumeDragging) // only continues, if volume dragging
            .map(ActionCreators.updateVolumeFromDrag)
    ));


const onVolumeSliderMouseDownEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.ON_VOLUME_SLIDER_MOUSE_DOWN)
    .pluck('payload', 'pageX')
    .map(ActionCreators.updateVolumeFromDrag);


const updateVolumeFromDragEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.UPDATE_VOLUME_FROM_DRAG)
    .pluck('payload')
    .map((mousePosX) => {
        const volumeControl = player.controlBar.getChild(Controls.VOLUME_CONTROL);
        const sliderWrapper = volumeControl.getChild(Controls.VOLUME_SLIDER_WRAPPER);
        const slider = sliderWrapper.getChild(Controls.VOLUME_SLIDER);
        const position = mousePosX - slider.el().getBoundingClientRect().left;
        let percentage = 100 * (position / slider.el().offsetWidth);

        if (percentage > 100) percentage = 100;
        if (percentage < 0) percentage = 0;

        return ActionCreators.setVolumeState(percentage / 100);
    });


/* AD countdown display */
const adCountdownEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.SET_AD_PLAYING_STATE)
    .pluck('payload')
    .filter(adPlaying => adPlaying)
    .do(() => player.addChild(Controls.AD_COUNTDOWN))
    .ignoreElements();


const adCountdownUpdateEpic = (action$, store, { player, options, ads }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_TIME_UPDATE)
    .throttleTime(100)
    .filter(() => !!store.getState().player.adPlaying)
    .map(() => player.getChild(Controls.AD_COUNTDOWN))
    .filter(adCountdown => !!adCountdown)
    .map(() => ads.getCurrentSlot())
    .filter(adSlot => !!adSlot)
    .do(adSlot => {
        const totalAdDuration = Math.round(adSlot.getTotalDuration());
        const timeRemaining = totalAdDuration - Math.round(adSlot.getPlayheadTime());
        const timeRemainingText = timecodeFromSeconds(timeRemaining);
        const adCountdownTimeRemaining = player.getChild(Controls.AD_COUNTDOWN).el().querySelector('.time-remaining');
        adCountdownTimeRemaining.innerHTML = timeRemainingText;
    })
    .ignoreElements();


const adCountdownRemoveEpic = (action$, store, { player, options }) => action$
    .ofType(ActionTypes.SET_AD_PLAYING_STATE)
    .pluck('payload')
    .filter(adPlaying => !adPlaying)
    .do(() => player.removeChild(Controls.AD_COUNTDOWN))
    .ignoreElements();



/* AD Midroll points on progress bar */
const adMidrollPointsEpic = (action$, store, { player, options, ads }) => action$
    .ofType(ActionTypes.PLAYER_EVENT_LOADED_DATA)
    .filter(() => !!store.getState().player.adsEnabled) // when ads are turned on
    .filter(() => !store.getState().player.adPlaying) // when an ad is not playing
    .filter(() => !!ads.midrolls.length) // only when there are midrolls
    .do(() => {
        const progressControl = player.controlBar.progressControl;
        const midrollClassName = 'midroll-dot';

        for (var i = 0; i < ads.midrolls.length; i++) {
            const slot = ads.midrolls[i];
            const slotTimePosition = slot.getTimePosition();
            let div = document.createElement('div');

            div.className = midrollClassName;
            div.style.left = (slotTimePosition / store.getState().videoSource.duration * 100) + '%';
            progressControl.el().appendChild(div);
        }
    })
    .ignoreElements();


const removeAdMidrollPointsEpic = (action$, store, { player, options, ads }) => Observable.merge(
        action$.ofType(ActionTypes.SET_VIDEO_SOURCE),
        action$.ofType(ActionTypes.ADS_MIDROLL_END),
    )
    .filter(() => !!store.getState().player.adsEnabled) // when ads are turned on
    .do(() => {
        const progressControl = player.controlBar.progressControl;
        const midrollClassName = 'midroll-dot';
        const previousMidrolls = progressControl.el().getElementsByClassName(midrollClassName);
        while (previousMidrolls.length > 0) { // remove previous midrolls from previous content
            previousMidrolls[0].parentNode.removeChild(previousMidrolls[0]);
        }
    })
    .ignoreElements();



// if the live state is newly changed and it's false but it should be a stream, throw up the reconnecting slate
const showReconnectingSlateEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.SET_LIVE_STATE)
    .pluck('payload')
    .distinctUntilChanged()
    .filter(isLive => isLive === false)
    .map(() => store.getState().videoSource.type)
    .filter(videoType => (videoType === VideoTypes.STREAM))
    .do(() => player.poster(RECONNECTING_SLATE)) // set the poster
    .do(() => player.pause()) // pause the content
    .delay(10000) // wait a few seconds
    .switchMapTo(Observable.of(
        ActionCreators.setLiveState(null), // reset live state so it knows to check again for a broken stream
        ActionCreators.reconnectStream() // try to reconnect the stream
    ));


const show403SlateEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.HLS_EVENT_LOAD_ERROR)
    .pluck('payload', 'networkDetails', 'status')
    .filter((status) => status === 403)
    .do(() => player.pause()) // pause the content
    .do(() => player.poster(FORBIDDEN_SLATE))
    .ignoreElements();


const show404SlateEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.HLS_EVENT_LOAD_ERROR)
    .pluck('payload', 'networkDetails', 'status')
    .filter(status => status === 404)
    .do(() => player.pause()) // pause the content
    .do(() => player.poster(NOT_FOUND_SLATE))
    .ignoreElements();


const showProblemSlateEpic = (action$, store, { player }) => action$
    .ofType(ActionTypes.HLS_EVENT_LOAD_ERROR)
    .pluck('payload', 'networkDetails', 'status')
    .filter(status => status !== 404 && status !== 403)
    .do(() => player.pause()) // pause the content
    .do(() => player.poster(PROBLEM_SLATE))
    .ignoreElements();


export default combineEpics(
    controlsAdultSwimReturnEpic,
    controlsFullscreenEpic,
    controlsInitEpic,
    controlsMuteEpic,
    controlsPauseEpic,
    controlsPlayEpic,
    onDocumentMouseMoveEpic,
    onDocumentMouseUpEpic,
    onVolumeControlMouseOutEpic,
    onVolumeControlMouseOverEpic,
    onVolumeSliderMouseDownEpic,
    updateVolumeFromDragEpic,
    adCountdownEpic,
    adCountdownUpdateEpic,
    adCountdownRemoveEpic,
    adMidrollPointsEpic,
    removeAdMidrollPointsEpic,
    showReconnectingSlateEpic,
    show403SlateEpic,
    show404SlateEpic,
    showProblemSlateEpic,
    shareControlInitEpic
);




// WEBPACK FOOTER //
// ./src/ui/epics.js